import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Mail, MessageSquare, Send, History } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const when = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const SendHistory = ({ history }) => (
  <div className="rounded-lg border border-slate-200 divide-y max-h-40 overflow-y-auto" data-testid="invoice-send-history">
    {history.length === 0 && <p className="text-xs text-slate-400 p-2">Not sent yet.</p>}
    {history.map((h) => (
      <div key={h.id} className="flex items-center gap-2 px-2 py-1.5 text-xs" data-testid="invoice-send-history-row">
        {h.channel === "sms" ? <MessageSquare className="w-3 h-3 text-emerald-600 shrink-0" /> : <Mail className="w-3 h-3 text-orange-600 shrink-0" />}
        <span className="truncate flex-1 text-slate-700">{h.recipient} <span className="text-slate-400">· {h.recipient_role === "primary_contact" ? "primary contact" : "customer"} · {h.kind}</span></span>
        <span className="text-slate-400 whitespace-nowrap">{when(h.sent_at)}</span>
        <Badge className={`${h.status === "sent" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"} hover:bg-inherit`} title={h.error || ""}>{h.status}</Badge>
      </div>
    ))}
  </div>
);

export const SendInvoiceDialog = ({ invoiceId, onClose, onSent }) => {
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState({});
  const [sending, setSending] = useState(false);
  useEffect(() => {
    axios.get(`${API}/invoices/${invoiceId}/send-options`).then((r) => {
      setData(r.data);
      setChecked(Object.fromEntries(r.data.options.map((o) => [o.key, o.default])));
    }).catch((e) => { toast.error(e.response?.data?.detail || "Could not load recipients"); onClose(); });
  }, [invoiceId]);
  const keys = Object.entries(checked).filter(([, v]) => v).map(([k]) => k);
  const send = async () => {
    setSending(true);
    try {
      const { data: res } = await axios.post(`${API}/invoices/${invoiceId}/send`, { keys });
      toast[res.failed?.length ? "warning" : "success"](res.message);
      onSent?.(res);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send invoice"); } finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-testid="send-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Send invoice {data?.invoice_number || ""}</DialogTitle>
          <DialogDescription>{data ? `${data.customer_name} · balance $${Number(data.balance || 0).toFixed(2)} · due ${data.due_date}` : "Loading…"}</DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-4">
            <div className="space-y-2">
              {data.options.map((o) => (
                <label key={o.key} className={`flex items-start gap-3 rounded-lg border p-3 ${o.available ? "border-slate-200 cursor-pointer hover:bg-slate-50" : "border-slate-100 bg-slate-50 opacity-70"}`} data-testid={`send-option-${o.key}`}>
                  <Checkbox checked={!!checked[o.key]} disabled={!o.available} onCheckedChange={(v) => setChecked({ ...checked, [o.key]: !!v })} className="mt-0.5" data-testid={`send-option-checkbox-${o.key}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 flex items-center gap-2">{o.channel === "sms" ? <MessageSquare className="w-4 h-4 text-emerald-600" /> : <Mail className="w-4 h-4 text-orange-600" />}{o.label}</p>
                    <p className="text-xs text-slate-600 truncate">{o.value || "—"}</p>
                    {!o.available && <p className="text-xs text-amber-700 mt-0.5" data-testid={`send-option-reason-${o.key}`}>{o.reason}</p>}
                  </div>
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-500">Primary-contact setting for this customer: <span className="font-medium">{{ both: "Both", email: "Email only", sms: "Text only", off: "Off" }[data.contact_delivery] || data.contact_delivery}</span> (change it on the customer).</p>
            <div>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 flex items-center gap-1"><History className="w-3 h-3" /> Send history</p>
              <SendHistory history={data.history} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="send-invoice-cancel">Cancel</Button>
          <Button onClick={send} disabled={!data || sending || keys.length === 0} className="bg-orange-600 hover:bg-orange-700" data-testid="send-invoice-submit"><Send className="w-4 h-4 mr-2" />{sending ? "Sending…" : `Send to ${keys.length} recipient${keys.length === 1 ? "" : "s"}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
